import React, { useState } from 'react';

export interface FacilitatorGuidance {
  id: string;
  type: string;
  content: string;
  timestamp: string;
}

interface GuidanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddGuidance: (guidance: Omit<FacilitatorGuidance, 'id' | 'timestamp'>) => void;
  guidanceHistory?: FacilitatorGuidance[];
}

const GUIDANCE_TYPES = [
  { value: 'constraint', label: '🚧 New Constraint Discovered', icon: '🚧' },
  { value: 'requirement', label: '✅ New Requirement', icon: '✅' },
  { value: 'stakeholder', label: '👥 Stakeholder Concern', icon: '👥' },
  { value: 'technical', label: '🔧 Technical Detail', icon: '🔧' },
  { value: 'business', label: '💼 Business Context', icon: '💼' },
  { value: 'redirect', label: '↪️ Redirect Discussion', icon: '↪️' },
  { value: 'clarification', label: '🎯 Clarification', icon: '🎯' }
];

export const GuidanceModal: React.FC<GuidanceModalProps> = ({
  isOpen,
  onClose,
  onAddGuidance,
  guidanceHistory = []
}) => {
  const [guidanceType, setGuidanceType] = useState('constraint');
  const [guidanceContent, setGuidanceContent] = useState('');

  if (!isOpen) return null;

  const handleAdd = () => {
    if (guidanceContent.trim()) {
      onAddGuidance({
        type: guidanceType,
        content: guidanceContent
      });
      setGuidanceContent('');
    }
  };

  const getTypeLabel = (type: string) => {
    const found = GUIDANCE_TYPES.find(t => t.value === type);
    return found ? found.label : type;
  };

  const charCount = guidanceContent.length;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.8)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <div style={{
        background: 'white',
        border: '2px solid #00c6c2',
        borderRadius: '15px',
        padding: '30px',
        maxWidth: '700px',
        width: '90%',
        maxHeight: '85vh',
        overflowY: 'auto'
      }}>
        <h2 style={{ color: '#052831', marginBottom: '15px' }}>
          💡 Add Facilitator Guidance
        </h2>
        <p style={{ color: '#052831', opacity: 0.7, marginBottom: '20px', fontSize: '0.9rem' }}>
          Add context or direction that emerged during the workshop. This guides avatar responses going forward.
        </p>

        {/* Guidance History */}
        {guidanceHistory.length > 0 && (
          <div style={{ marginBottom: '25px' }}>
            <h3 style={{ color: '#052831', fontSize: '0.95rem', marginBottom: '12px' }}>
              Recent Guidance ({guidanceHistory.length})
            </h3>
            <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
              {guidanceHistory.slice(-5).reverse().map(guidance => (
                <div
                  key={guidance.id}
                  style={{
                    background: '#fdf7ea',
                    border: '2px solid #00c6c2',
                    borderRadius: '8px',
                    padding: '10px',
                    marginBottom: '8px'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '6px'
                  }}>
                    <span style={{ fontSize: '0.8rem', color: '#052831', fontWeight: 600 }}>
                      {getTypeLabel(guidance.type)}
                    </span>
                    <span style={{ fontSize: '0.75rem', color: '#052831', opacity: 0.6 }}>
                      {new Date(guidance.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.9rem', color: '#052831' }}>
                    {guidance.content}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Add New Guidance */}
        <div style={{
          background: '#fdf7ea',
          border: '2px solid #00c6c2',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '20px'
        }}>
          <h3 style={{ color: '#052831', fontSize: '1rem', marginBottom: '15px' }}>
            Add New Guidance
          </h3>

          <div style={{ marginBottom: '15px' }}>
            <label style={{
              display: 'block',
              color: '#052831',
              marginBottom: '8px',
              fontSize: '0.9rem',
              fontWeight: 600
            }}>
              Guidance Type
            </label>
            <select
              value={guidanceType}
              onChange={(e) => setGuidanceType(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: '2px solid #00c6c2',
                background: 'white',
                color: '#052831',
                fontSize: '0.95rem',
                fontFamily: 'inherit',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              {GUIDANCE_TYPES.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '12px' }}>
            <label style={{
              display: 'block',
              color: '#052831',
              marginBottom: '8px',
              fontSize: '0.9rem',
              fontWeight: 600
            }}>
              Guidance (What should avatars know?)
            </label>
            <textarea
              value={guidanceContent}
              onChange={(e) => setGuidanceContent(e.target.value)}
              placeholder="e.g., 'The budget is actually £50k not £100k' or 'Need to prioritize mobile-first design'"
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: '2px solid #00c6c2',
                background: 'white',
                color: '#052831',
                fontSize: '0.95rem',
                fontFamily: 'inherit',
                minHeight: '100px',
                resize: 'vertical',
                outline: 'none'
              }}
            />
            <div style={{ color: '#052831', opacity: 0.6, fontSize: '0.8rem', marginTop: '5px' }}>
              {charCount} characters
            </div>
          </div>

          <button
            onClick={handleAdd}
            disabled={!guidanceContent.trim()}
            style={{
              width: '100%',
              padding: '10px',
              borderRadius: '8px',
              border: 'none',
              background: guidanceContent.trim()
                ? '#00c6c2'
                : 'rgba(5, 40, 49, 0.3)',
              color: guidanceContent.trim() ? '#052831' : 'white',
              fontWeight: 600,
              cursor: guidanceContent.trim() ? 'pointer' : 'not-allowed',
              opacity: guidanceContent.trim() ? 1 : 0.5,
              transition: 'all 0.2s',
              fontSize: '0.95rem'
            }}
          >
            ➕ Add Guidance
          </button>
        </div>

        <button
          onClick={onClose}
          style={{
            width: '100%',
            padding: '12px',
            borderRadius: '8px',
            background: 'white',
            border: '2px solid #052831',
            color: '#052831',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.2s',
            fontSize: '1rem'
          }}
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default GuidanceModal;
